import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CustomMatPaginatorIntl } from '@core/classes/custom-mat-paginator-intl';
import { BUILD_TIME } from '@core/environments/build-info';
import { environment } from '@core/environments/environment';
import { EEnvironmentName } from '@core/environments/environment.defaults';
import { ApiInterceptor } from '@core/interceptors/api-interceptor.interceptor';
import { AppUpdateService } from '@core/services/app-update.service';
import { GlobalErrorHandler } from '@core/services/error-handler.service';
import { TranslationBootService } from '@core/services/translation-boot.service';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { LOCALE_MAP } from '@shared/constants/locale.constants';
import { CUSTOM_DATE_FORMATS } from '@utilities/date-utilities.service';
import { NgxEchartsModule } from 'ngx-echarts';
import { provideNgxMask } from 'ngx-mask';
import { provideQuillConfig } from 'ngx-quill';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { TimeagoClock, TimeagoCustomFormatter, TimeagoFormatter, TimeagoIntl, TimeagoModule } from 'ngx-timeago';
import { Observable } from 'rxjs';
import { ELanguage } from './app.interface';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideNgxMask(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: shouldEnableServiceWorker(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          deps: [HttpClient],
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
        },
      }),
      MatSnackBarModule,
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      }),
    ),
    {
      deps: [Router],
      provide: Sentry.TraceService,
    },
    {
      // Sentry.TraceService is a plain ConstructorProvider (see above) that Angular only ever
      // constructs if something injects it - it has no consumer of its own, so it's listed
      // here purely to force eager instantiation, exactly like the old translation
      // initializer used to do. Losing this silently (as happened once already while this
      // file was being edited) doesn't break anything visibly; it just quietly stops Sentry's
      // router-based tracing/breadcrumbs from ever starting.
      deps: [TranslationBootService, Sentry.TraceService],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: translationBootInitializerFactory,
    },
    {
      deps: [AppUpdateService],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: appUpdateInitializerFactory,
    },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: MatPaginatorIntl, useFactory: () => new CustomMatPaginatorIntl(inject(TranslateService)) },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAnimations(),
    { provide: DateAdapter, useClass: DateFnsAdapter },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (): string => {
        const translateService = inject(TranslateService);
        const currentLang = (translateService.currentLang || translateService.defaultLang || ELanguage.en) as ELanguage;

        return LOCALE_MAP[currentLang] || LOCALE_MAP[ELanguage.en];
      },
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ],
      },
    }),
    TimeagoIntl,
    importProvidersFrom(
      TimeagoModule.forRoot({
        clock: {
          provide: TimeagoClock,
          useValue: {
            tick: (then: number) => {
              return new Observable((observer) => {
                const timer = setInterval(() => {
                  observer.next(Date.now() - then);
                }, 45000);

                return () => clearInterval(timer);
              });
            },
          },
        },
        // The custom formatter reads TimeagoIntl's strings, which TranslationBootService keeps on the UI language.
        formatter: { provide: TimeagoFormatter, useClass: TimeagoCustomFormatter },
      }),
    ),
    provideNgxSkeletonLoader({
      theme: {
        backgroundColor: 'var(--shikenso-gray-300)',
        extendsFromRoot: true,
      },
    }),
  ],
};

function httpLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient, 'i18n/', `.json?v=${BUILD_TIME}`);
}

// TranslationBootService boots translations itself, from its own constructor, the moment
// Angular first instantiates it (see the service for why: bundled English renders instantly,
// the user's actual selected language loads over HTTP with a bounded wait, and none of it
// blocks bootstrap). This APP_INITIALIZER entry exists only to force that instantiation to
// happen as part of app startup rather than whenever something else first happens to inject
// it - it does no work of its own and, critically, returns void rather than a Promise, so it
// never blocks bootstrap the way the old translation initializer used to.
function translationBootInitializerFactory(
  translationBoot: TranslationBootService,
  traceService: Sentry.TraceService,
): () => void {
  return (): void => {
    void translationBoot;
    void traceService;
  };
}

function appUpdateInitializerFactory(appUpdateService: AppUpdateService): () => void {
  return (): void => {
    appUpdateService.init();
  };
}

function shouldEnableServiceWorker(): boolean {
  const environmentName: EEnvironmentName = environment.environmentName ?? EEnvironmentName.local;

  return environmentName !== EEnvironmentName.local;
}
